import { motion } from "framer-motion";
import { ArrowDown, ArrowUpRight, Linkedin, MapPin, CalendarCheck, Repeat } from "lucide-react";
import { profil } from "../../data/content";
import { scrollToId } from "./Nav";
import { EcosystemGraphic } from "./EcosystemGraphic";
import { CVMenu } from "./CVMenu";

const easeReveal = [0.76, 0, 0.24, 1];

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay, ease: easeReveal }}
    >
      {children}
    </motion.span>
  </span>
);

const fadeUp = (delay) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] },
});

export const Hero = () => {
  return (
    <section id="accueil" className="relative min-h-screen flex items-center pt-28 pb-16 overflow-hidden grain">
      {/* Abstract ecosystem visual */}
      <div className="absolute inset-0 z-0">
        <EcosystemGraphic />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_40%,rgba(0,229,255,0.08),transparent_55%)]" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#05070A]/40 via-[#05070A]/70 to-[#05070A]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#05070A] via-[#05070A]/60 to-transparent" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full">
        <motion.div {...fadeUp(0.3)} className="flex items-center gap-4 mb-8">
          <div className="relative shrink-0">
            <div className="absolute -inset-1.5 rounded-full bg-cyan/25 blur-md" />
            <img
              src="/andrew.png"
              alt="Andrew Picornell, portrait"
              className="relative h-14 w-14 md:h-[68px] md:w-[68px] rounded-full object-cover object-top border border-cyan/50"
              data-testid="hero-photo"
            />
            <span className="absolute bottom-0 right-0 h-3.5 w-3.5 rounded-full bg-cyan border-2 border-[#05070A]" />
          </div>
          <div className="flex items-center gap-3">
            <span className="h-1.5 w-1.5 rounded-full bg-cyan animate-pulse" />
            <span className="text-[11px] uppercase tracking-[0.35em] text-[var(--text-2)]">
              {profil.ecole}
            </span>
          </div>
        </motion.div>

        <h1 className="font-display font-medium tracking-tight leading-[0.92] text-[13vw] sm:text-[9vw] lg:text-[6.6vw]">
          <Line delay={0.5}>Andrew</Line>
          <Line delay={0.62}>
            <span className="text-stroke">PICORNELL</span>
          </Line>
        </h1>

        <motion.p
          {...fadeUp(1.05)}
          className="mt-8 max-w-2xl text-lg md:text-xl text-white/90 font-body leading-relaxed"
        >
          {profil.titre.split("l'IA")[0]}
          <span className="font-serif-italic text-cyan">l'IA</span>
        </motion.p>

        <motion.p {...fadeUp(1.18)} className="mt-4 max-w-xl text-[var(--text-2)] font-body leading-relaxed">
          {profil.valeur}
        </motion.p>

        {/* Meta pills */}
        <motion.div {...fadeUp(1.3)} className="mt-8 flex flex-wrap gap-2.5">
          {[
            { icon: Repeat, text: profil.rythme },
            { icon: CalendarCheck, text: profil.disponibilite },
            { icon: MapPin, text: profil.mobilite },
          ].map((m, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-2 text-xs md:text-sm text-white/80 border border-white/10 rounded-full px-3.5 py-1.5 glass"
            >
              <m.icon size={14} className="text-cyan" /> {m.text}
            </span>
          ))}
        </motion.div>

        {/* CTAs */}
        <motion.div {...fadeUp(1.45)} className="mt-10 flex flex-wrap items-center gap-3">
          <button
            onClick={() => scrollToId("profil")}
            className="group inline-flex items-center gap-2 bg-cyan text-[#05070A] font-medium px-6 py-3.5 rounded-full hover:bg-white transition-colors duration-300"
            data-testid="hero-discover-btn"
          >
            Découvrir mon profil
            <ArrowDown size={17} className="group-hover:translate-y-0.5 transition-transform duration-300" />
          </button>
          <CVMenu variant="outline" label="Télécharger mon CV" testid="hero-cv" align="left" />
          <button
            onClick={() => scrollToId("contact")}
            className="inline-flex items-center gap-2 text-white/80 px-4 py-3.5 hover:text-white transition-colors duration-300"
            data-testid="hero-contact-btn"
          >
            Me contacter
          </button>
          <a
            href={profil.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center h-11 w-11 rounded-full border border-white/15 text-white/80 hover:text-cyan hover:border-cyan/60 transition-colors duration-300"
            data-testid="hero-linkedin"
            aria-label="LinkedIn"
          >
            <Linkedin size={18} />
          </a>
        </motion.div>
      </div>

      <motion.div
        {...fadeUp(1.8)}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-2"
      >
        <span className="text-[10px] uppercase tracking-[0.3em] text-[var(--muted)]">Défiler</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={16} className="text-[var(--text-2)]" />
        </motion.div>
      </motion.div>
    </section>
  );
};
